import React from "react";
import { DatePicker } from "../../../../../../../../../react-core/Datepicker/DatePicker";
import { FormComponent } from "../../../../../../../../../react-core/Html/FormComponent";
import { PartLoading } from "../../../../../../../../../react-core/Html/PartLoading";
import { SelectEditor } from "../../../../../../../../../react-core/Html/SelectEditor";
import { TextBoxEditor } from "../../../../../../../../../react-core/Html/TextBoxEditor";
import { Name50 } from "../../../../../../../../../react-core/Html/Validation/RegExPatterns";
import { JsonDateParser } from "../../../../../../../../../react-core/Utils/JsonDateParser";
import { UserClaim } from "../../../../../../../../../Wrap/Constants/Enums/UserClaim";
import { AccountStatus } from "../../../../../../../../../Wrap/Models/Domains/Account/SlAccounts/AccountStatus";
import { ClientAccount } from "../../../../../../../../../Wrap/Models/Domains/Account/SlAccounts/ClientAccount";
import { InvestorType } from "../../../../../../../../../Wrap/Models/Domains/Account/SlAccounts/InvestorType";
import { DeathDetails } from "../../../../../../../../../Wrap/Models/Models/Customer/DeathDetails";
import {
    ContentDatePicker,
    ACCEPT_DATE_FORMATS,
} from "../../../../../../../../Shared/Entities/Constants/DateConstants";
import { checkPermission } from "../../../../../../../../Shared/Permission/PermissionHelper";
import { ValidatorContents } from "../../../../../../../Common/ValidatorHelper";
import { AccountInfoAction } from "../../Action/AccountInfoAction";
import { investorTypeMap } from "../../Resource/Contents";
import { RestrictionReasonAction } from "../../../../Action/RestrictionReasonAction";
import { ClientRecordAction } from "../../../../../ClientRecord/Action/ClientRecordAction";
import { accountStatusMap } from "../../../../../ClientRecord/Resource/Contents";
import { ClientRecordStore } from "../../../../../ClientRecord/Store/Variable";
import { portfolioDashboardRolesAction } from "../../Action/Action";
import { RDRClient } from "../../../../../../../../../Wrap/Models/Domains/Account/SlAccounts/RDRClient";

interface IAccountDetailsProps {
    accountHierarchyId: string;
    customerId: number;
}

interface IAccountDetailsState {
    clientAccount: ClientAccount;
    originalClientAccount: ClientAccount;
    isEdit: boolean;
    deathDetail: DeathDetails;
    dateOfDeath: Date;
    dateOfNotification: Date;
    clientRDR: RDRClient;
}

export class AccountDetails extends FormComponent<IAccountDetailsProps, IAccountDetailsState> {
    constructor(props: IAccountDetailsProps) {
        super(props);
        this.state = {
            clientAccount: null,
            originalClientAccount: null,
            isEdit: false,
            dateOfDeath: null,
            dateOfNotification: null,
            deathDetail: null,
            clientRDR: null,
        };
        this.subscribeToStore(ClientRecordStore, () => this.onStoreChange());
    }

    private onStoreChange() {
        const data = ClientRecordStore.getData();

        if (data.DeathDetail) {
            this.setState({
                dateOfDeath: JsonDateParser.toDate(data.DeathDetail.DateOfDeath),
                dateOfNotification: JsonDateParser.toDate(data.DeathDetail.NotificationDate),
                deathDetail: data.DeathDetail,
            });
        }
    }

    componentDidMount() {
        this.getData();
    }

    getData() {
        AccountInfoAction.getClientAccount(this.props.accountHierarchyId, (result) => {
            this.setState({ clientAccount: result, originalClientAccount: { ...result } });
            if ([AccountStatus.Deceased, AccountStatus.DeceasedClosed].includes(result.AccountStatus)) {
                ClientRecordAction.getDeathDetail(this.props.customerId.toString());
            }
            AccountInfoAction.getClientRDR(result.HeadClAccountId, (data) => {
                this.setState({ clientRDR: data });
            });
        });
    }

    private datePickerOptions = (hasValidate: boolean, particularDate?: Date, particularMessage?: string) => {
        return {
            showYearDropdown: true,
            todayButton: ContentDatePicker.Today,
            isClearable: true,
            atMost: hasValidate
                ? {
                      value: particularDate != null ? particularDate : new Date(),
                      errorMessage: particularMessage != null ? particularMessage : "Date can not be in the future",
                  }
                : null,
            lessThan: null,
            dateFormat: ContentDatePicker.DateFormat,
            acceptDateFormats: ACCEPT_DATE_FORMATS,
            formGroupCssWrapper: "control-size-300",
        };
    };

    private futerDatePickerOptions = (hasValidate: boolean, particularDate?: Date, particularMessage?: string) => {
        return {
            showYearDropdown: true,
            todayButton: ContentDatePicker.Today,
            isClearable: true,
            atLeast:
                hasValidate && particularDate != null
                    ? {
                          value: particularDate != null ? particularDate : new Date("0001-01-01"),
                          errorMessage: particularDate != null ? particularMessage : "Date input is invalid",
                      }
                    : null,
            dateFormat: ContentDatePicker.DateFormat,
            acceptDateFormats: ACCEPT_DATE_FORMATS,
            formGroupCssWrapper: "control-size-300",
        };
    };

    private saveData() {
        if (this.isValid()){
            this.setState({ isEdit: false });
            portfolioDashboardRolesAction.updateClientAccountDetails(
                this.state.clientAccount,
                this.state.dateOfDeath,
                this.state.dateOfNotification,
                (result) => {
                    this.getData();
                    RestrictionReasonAction.getAccountRestrictions(this.props.customerId);
                    portfolioDashboardRolesAction.updateClientAccountDetailsToStore(this.state.clientAccount);
                }
            );
        }
    }

    private isPreDR = (feeStructure: string): boolean => {
        return feeStructure === "Commission";
    };

    render() {
        const { clientAccount, originalClientAccount, isEdit } = this.state;
        if (!clientAccount) {
            return <PartLoading isLoading={true} />;
        }
        const canEditAccountInfo = checkPermission(UserClaim.AdviserPortalPortfolioViewRolesReadWrite);

        return (
            <>
                <h4>Account information</h4>
                {!isEdit && (
                    <dl className="def-list">
                        <dt>Account name</dt>
                        <dd>{clientAccount.AccountName}</dd>
                        <dt>Account type</dt>
                        <dd>
                            {clientAccount.InvestorType == InvestorType.JointIndividual
                                ? "Joint"
                                : investorTypeMap[clientAccount.InvestorType]}
                        </dd>
                        <dt>Adviser name</dt>
                        <dd>{clientAccount.AdviserName}</dd>
                        <dt>Adviser code</dt>
                        <dd>{clientAccount.AdviserCode}</dd>
                        <dt>Agency code</dt>
                        <dd>{clientAccount.CompanyAdvCode}</dd>
                        <dt>Company name</dt>
                        <dd>{clientAccount.CompanyName}</dd>
                        <dt>Advice type</dt>
                        <dd>{clientAccount.Discretionary ? "Discretionary" : "Non-discretionary"}</dd>
                        <dt>Account status</dt>
                        <dd>{accountStatusMap[clientAccount.AccountStatus]}</dd>
                        <dt>Date of closure</dt>
                        <dd>
                            {JsonDateParser.getFormatDateString(
                                JsonDateParser.toDate(clientAccount.ClosedDate),
                                "dd-MM-yyyy",
                                false
                            )}
                        </dd>
                        <dt>Inception Date</dt>
                        <dd>
                            {JsonDateParser.getFormatDateString(
                                JsonDateParser.toDate(clientAccount.InceptionDate),
                                "dd-MM-yyyy",
                                false
                            )}
                        </dd>
                        <dt>Commission Paying</dt>
                        <dd>{this.isPreDR(this.state.clientRDR?.FeeStructure) ? "Yes" : "No"}</dd>
                        <dt>Date commission stopped being paid</dt>
                        <dd>
                            {this.state.clientRDR?.SwitchDate
                                ? JsonDateParser.getFormatDateString(
                                      JsonDateParser.toDate(this.state.clientRDR?.SwitchDate),
                                      "dd-MM-yyyy",
                                      false
                                  )
                                : "N/A"}
                        </dd>
                        <dt>Hold Execution of Orders For SIPP</dt>
                        <dd>{clientAccount.RestrictActivities ? "Yes" : "No"}</dd>
                    </dl>
                )}
                {isEdit && (
                    <>
                        <TextBoxEditor
                            label="Account name"
                            required="Please enter account name"
                            value={clientAccount.AccountName}
                            maxLength={45}
                            disabled={!canEditAccountInfo}
                            regex={{
                                errorMessage: ValidatorContents.Name50,
                                value: Name50,
                            }}
                            onChange={(v) => {
                                clientAccount.AccountName = v;
                                this.setState({ clientAccount: clientAccount });
                            }}
                            cssWrapper={"control-size-245"}
                        />
                        <SelectEditor
                            getText={(v) => {
                                return accountStatusMap[v];
                            }}
                            selected={clientAccount.AccountStatus}
                            onChange={(v) => {
                                clientAccount.AccountStatus = v;
                                this.setState({
                                    clientAccount: clientAccount,
                                    dateOfDeath: JsonDateParser.toDate(this.state.deathDetail?.DateOfDeath),
                                    dateOfNotification: JsonDateParser.toDate(this.state.deathDetail?.NotificationDate),
                                });
                            }}
                            disabled={!canEditAccountInfo}
                            label="Account Status"
                            options={Object.keys(accountStatusMap).map((ele) => Number(ele))}
                            cssWrapper={"control-size-245"}
                        />
                        {[AccountStatus.Deceased, AccountStatus.DeceasedClosed].includes(
                            clientAccount.AccountStatus
                        ) && (
                            <>
                                <DatePicker
                                    selected={this.state.dateOfNotification}
                                    label={"Death notification on"}
                                    onChange={(v) => {
                                        this.setState({ dateOfNotification: v });
                                    }}
                                    {...this.futerDatePickerOptions(
                                        true,
                                        JsonDateParser.toDateNoOffset(this.state.dateOfDeath),
                                        "Death notification date cannot be smaller than death date of " +
                                            JsonDateParser.toDateNoOffset(this.state.dateOfDeath)?.toDateString()
                                    )}
                                />
                                <DatePicker
                                    selected={this.state.dateOfDeath}
                                    label={"Death on"}
                                    onChange={(v) => {
                                        this.setState({ dateOfDeath: v });
                                    }}
                                    {...this.datePickerOptions(
                                        true,
                                        JsonDateParser.toDateNoOffset(this.state.dateOfNotification),
                                        "Date of death must be the same day or before the notification date" +
                                            JsonDateParser.toDateNoOffset(this.state.dateOfNotification)?.toDateString()
                                    )}
                                />
                            </>
                        )}
                        <SelectEditor
                            getText={(v) => {
                                return v ? "Discretionary" : "Non-discretionary";
                            }}
                            selected={clientAccount.Discretionary}
                            onChange={(v) => {
                                clientAccount.Discretionary = v;
                                this.setState({ clientAccount: clientAccount });
                            }}
                            disabled={!canEditAccountInfo}
                            label="Advice type"
                            options={[true, false]}
                            cssWrapper={"control-size-245"}
                        />
                    </>
                )}
                {canEditAccountInfo && (
                    <div>
                        <div className="row row-center">
                            {!isEdit && (
                                <div className="col-auto">
                                    <button
                                        className="edit-circle-icon-button label-sr-only"
                                        title="Edit"
                                        onClick={(val) => this.setState({ isEdit: true })}
                                    >
                                        <span className="icon" aria-hidden="true"></span>
                                        <span className="text">Edit</span>
                                    </button>
                                </div>
                            )}
                            {isEdit && (
                                <div className="col-auto">
                                    <button
                                        className="button mr-20"
                                        onClick={() => {
                                            this.setState({
                                                isEdit: false,
                                                clientAccount: { ...originalClientAccount },
                                                dateOfDeath: JsonDateParser.toDate(this.state.deathDetail?.DateOfDeath),
                                                dateOfNotification: JsonDateParser.toDate(
                                                    this.state.deathDetail?.NotificationDate
                                                ),
                                            });
                                        }}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            )}
                            {isEdit && (
                                <div className="col-auto">
                                    <button className="button" onClick={(val) => this.saveData()}>
                                        Save
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </>
        );
    }
}
